import { useContext, useEffect, useState, useCallback } from 'react';
import { UserContext } from '../context/UserContext';
import { useBioData } from '../context/BioDataContext';
import { useScenarios } from '../context/ScenarioContext';
import { useSettings } from '../context/SettingsContext';
import { 
  saveBioData, 
  saveSettings,
  saveScenario
} from '../services/firestore';

/**
 * Custom hook to manage user data synchronization with Firestore
 */
const useUserData = () => {
  const { user } = useContext(UserContext);
  const bioDataContext = useBioData();
  const scenarioContext = useScenarios();
  const settingsContext = useSettings();
  
  // Handle cases where contexts might be undefined
  const { bioData, setBioData } = bioDataContext || {};
  const { scenarios, setScenarios } = scenarioContext || {};
  const { 
    globalSettings, 
    expandedSections, 
    visibilitySettings, 
    scenarioVisibility 
  } = settingsContext || {};
  
  const [isLoading, setIsLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);

  // ALL AUTOMATIC OPERATIONS DISABLED
  // - No auto-save
  // - No auto-load
  // - Manual operations only
  
  // Save user data to Firestore
  const saveData = useCallback(async () => {
    if (!user || user.isGuest) {
      return;
    }
    
    // Get current context values at time of save (use already extracted values)
    const currentBioData = bioData;
    const currentScenarios = scenarios;
    const currentGlobalSettings = globalSettings;
    const currentExpandedSections = expandedSections;
    const currentVisibilitySettings = visibilitySettings;
    const currentScenarioVisibility = scenarioVisibility;
    
    // Combine all settings into a single object for saving
    const allSettings = {
      globalSettings: currentGlobalSettings,
      expandedSections: currentExpandedSections,
      visibilitySettings: currentVisibilitySettings,
      scenarioVisibility: currentScenarioVisibility
    };
    
    
    setIsLoading(true);
    setError(null);
    
    try {
      let saveCount = 0;
      
      // Save bio data
      if (currentBioData && Object.keys(currentBioData).length > 0) {
        try {
          await saveBioData(user.uid, currentBioData);
          saveCount++;
        } catch (bioError) {
          console.error('Failed to save bio data:', bioError);
        }
      }
      
      // Save scenarios
      if (currentScenarios && currentScenarios.length > 0) {
        try {
          for (const scenario of currentScenarios) {
            await saveScenario(user.uid, scenario);
          }
          saveCount++;
        } catch (scenarioError) {
          console.error('Failed to save scenarios:', scenarioError);
        }
      }
      
      // Save settings
      if (allSettings && Object.keys(allSettings).some(key => allSettings[key])) {
        try {
          await saveSettings(user.uid, allSettings);
          saveCount++;
        } catch (settingsError) {
          console.error('Failed to save settings:', settingsError);
        }
      }
      
      if (saveCount > 0) {
        const saveTime = new Date();
        setLastSaved(saveTime);
        // console.log('Save completed - ' + saveCount + ' data types saved');
      } else {
        setError("No data to save. Please check that you have entered some information.");
      }
    } catch (err) {
      console.error("Error saving user data:", err);
      setError("Failed to save your data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [user, bioData, scenarios, globalSettings, expandedSections, visibilitySettings, scenarioVisibility]); // Include all data dependencies
  
  // AUTO-SAVE DISABLED - Manual save only via save button
  // The auto-save was causing infinite loops, so we've switched to manual saves only
  
  return {
    isLoading,
    lastSaved,
    error,
    saveData, // Manual save function
    isGuest: user?.isGuest || !user
  };
};

export default useUserData;
